import { useTranslation } from 'react-i18next';
import {
    Box,
    VStack,
    HStack,
    Text,
    Badge,
    Switch,
    Divider,
    IconButton,
    Tooltip,
    Code,
} from '@chakra-ui/react';
import { AppDialog } from '../common/AppDialog';
import { useMemo } from 'react';
import { FiPackage, FiTrash2 } from 'react-icons/fi';
import { usePluginStore, PluginInstance } from '../../plugins/PluginSystem';
import { useAppStore } from '../../state';

/**
 * Plugin Manager Trigger
 *
 * The toolbar-side trigger of the plugin manager. The dialog itself lives in
 * PluginManagerDialog (controlled by useAppStore.pluginManagerOpen) so it can
 * also be opened from the suite topbar actions in BIM mode, where this trigger
 * is not mounted.
 */
export function PluginManager() {
    const { t } = useTranslation();
    const setPluginManagerOpen = useAppStore((s) => s.setPluginManagerOpen);

    return (
        <Tooltip label={t('plugins.plugins')}>
            <IconButton
                icon={<FiPackage />}
                variant="ghost"
                size="sm"
                aria-label={t('plugins.plugins')}
                onClick={() => setPluginManagerOpen(true)}
            />
        </Tooltip>
    );
}

/**
 * Plugin Manager Dialog
 *
 * Provides UI for:
 * - Viewing installed plugins
 * - Enabling/disabling plugins
 * - Plugin details
 */
export function PluginManagerDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.pluginManagerOpen);
    const setPluginManagerOpen = useAppStore((s) => s.setPluginManagerOpen);
    const onClose = () => setPluginManagerOpen(false);
    // Subscribe to the stable plugins Map and materialize the list in a memo;
    // the old `s.getAllPlugins()` selector built a fresh array per snapshot.
    const pluginMap = usePluginStore((s) => s.plugins);
    const plugins = useMemo(() => Array.from(pluginMap.values()), [pluginMap]);
    const enablePlugin = usePluginStore((s) => s.enablePlugin);
    const disablePlugin = usePluginStore((s) => s.disablePlugin);
    const unregisterPlugin = usePluginStore((s) =>s.unregisterPlugin); return (
            <AppDialog
                isOpen={isOpen}
                onClose={onClose}
                size="lg"
                icon={<FiPackage />}
                title={t('plugins.pluginManager')}
            >
                <HStack>
                    <Badge colorScheme="blue">{plugins.length}</Badge>
                </HStack>
                <VStack spacing={4} align="stretch">
                    {plugins.length === 0 ? (
                        <Box textAlign="center" py={8}>
                            <Text color="var(--color-text-muted)">{t('plugins.noPluginsInstalled')}</Text>
                        </Box>
                    ) : (
                        plugins.map((plugin) => (
                            <PluginCard
                                key={plugin.manifest.id}
                                plugin={plugin}
                                onToggle={(enabled) => {
                                    if (enabled) {
                                        enablePlugin(plugin.manifest.id);
                                    } else {
                                        disablePlugin(plugin.manifest.id);
                                    }
                                }}
                                onUninstall={() => unregisterPlugin(plugin.manifest.id)}
                            />
                        ))
                    )}

                    <Divider />

                    <Box>
                        <Text fontSize="xs" color="var(--color-text-muted)" mb={2}>{t('plugins.toInstallAPluginUseTheConsole')}</Text>
                        <Code fontSize="xs" p={2} borderRadius="md" display="block">
                            {`usePluginStore.getState().registerPlugin(myPlugin)`}
                        </Code>
                    </Box>
                </VStack>
            </AppDialog>
    );
}

/**
 * Plugin Card Component
 */
function PluginCard({
    plugin,
    onToggle,
    onUninstall,
}: {
    plugin: PluginInstance;
    onToggle: (enabled: boolean) => void;
    onUninstall: () => void;
}) {
    const { t } = useTranslation();
    const { manifest, enabled } = plugin;

    return (
        <Box
            p={4}
            bg="surface.300"
            borderRadius="md"
            border="1px solid"
            borderColor={enabled ? 'whiteAlpha.200' : 'whiteAlpha.100'}
            opacity={enabled ? 1 : 0.7}
        >
            <HStack justify="space-between" mb={2}>
                <HStack>
                    <Text fontWeight="semibold">{manifest.name}</Text>
                    <Badge colorScheme="gray" fontSize="xs">
                        v{manifest.version}
                    </Badge>
                </HStack>

                <HStack>
                    <Switch
                        isChecked={enabled}
                        onChange={(e) => onToggle(e.target.checked)}
                        colorScheme="green"
                        size="sm"
                    />
                    <Tooltip label={t('plugins.uninstall')}>
                        <IconButton
                            icon={<FiTrash2 />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            aria-label={t('plugins.uninstall')}
                            onClick={onUninstall}
                        />
                    </Tooltip>
                </HStack>
            </HStack>

            <Text fontSize="sm" color="var(--color-text-muted)" mb={2}>
                {manifest.description}
            </Text>

            <HStack fontSize="xs" color="var(--color-text-muted)" spacing={4}>
                <Text>By {manifest.author}</Text>
                {manifest.tools && (
                    <Badge size="sm" colorScheme="blue">
                        {manifest.tools.length} tools
                    </Badge>
                )}
                {manifest.commands && (
                    <Badge size="sm" colorScheme="green">
                        {manifest.commands.length} commands
                    </Badge>
                )}
            </HStack>
        </Box>
    );
}
